import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, CalendarDays } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { Reveal, SectionHeading } from "./Section";

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);
  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <section id="gallery" className="relative py-28 md:py-36 bg-[#0C0F16]" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading index="09" eyebrow="Gallery" title="Moments that built the journey." subtitle="A visual timeline of people, partnerships, countries, companies, milestones and everything behind the scenes." />
        <Reveal>
          <div className="mb-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {categories.map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/\s|&/g, "-")}`} className={`rounded-full border px-4 py-2 text-xs font-medium tracking-wide transition-colors ${filter === c ? "border-[#E2C08D] bg-[#E2C08D]/15 text-[#E2C08D]" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                {c}
              </button>
            ))}
          </div>
        </Reveal>
        <motion.div layout className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {visible.map((g) => (
              <motion.button
                layout key={g.id}
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                onClick={() => setLightbox(g)}
                className="group relative overflow-hidden rounded-2xl border border-white/8 text-left"
                data-testid={`gallery-item-${g.id.slice(0, 8)}`}
              >
                <img src={imageUrl(g.url)} alt={g.caption || g.category} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#E2C08D]">{g.category}</span>
                  <p className="mt-1 text-sm font-medium text-slate-100">{g.caption}</p>
                  {(g.date || g.location) && (
                    <p className="mt-1.5 flex items-center gap-3 font-mono text-[11px] text-slate-400">
                      {g.date && <span className="inline-flex items-center gap-1"><CalendarDays size={11} />{g.date}</span>}
                      {g.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{g.location}</span>}
                    </p>
                  )}
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
        {visible.length === 0 && <p className="text-sm text-slate-500" data-testid="gallery-empty">Photos will appear here soon.</p>}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-5" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
            <button className="absolute top-5 right-5 text-slate-300 hover:text-white" data-testid="gallery-lightbox-close" aria-label="Close"><X size={26} /></button>
            <motion.figure initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <img src={imageUrl(lightbox.url)} alt={lightbox.caption} className="max-h-[75vh] w-auto rounded-xl border border-white/10 object-contain" />
              <figcaption className="mt-4 text-center text-sm text-slate-300">{lightbox.caption} {lightbox.location && <span className="text-slate-500">· {lightbox.location}</span>}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
